// src/features/executive/insight/widgets/CategoricalBar.tsx
import { Box, Button, Stack, Typography } from '@mui/material'
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import WidgetCard from 'features/executive/components/WidgetCard/WidgetCard'
import { formatVariance } from 'utils/executive.utils'
import { sliceVisible } from 'features/executive/shared/utils/chart-mode'

function varianceOf(current: number, previous: number | null | undefined): number | null {
  if (previous == null || previous === 0) return null
  return (current - previous) / Math.abs(previous)
}

export interface CategoricalBarRow {
  key: string
  label: string
  count: number
  percent: number
  // Same-duration previous-window count for the per-row trend arrow.
  previousCount?: number | null
  // Per-row bar colour; falls back to the component-level barColor.
  color?: string
}

export interface CategoricalBarProps {
  title: string
  titleTooltip?: string
  subtitle?: ReactNode
  notice?: ReactNode
  headerAction?: ReactNode
  emptyMessage?: string
  rows: CategoricalBarRow[]
  barColor?: string      // default '#2a8d9c'
  formatValue?: (row: CategoricalBarRow) => string
  // When set and rows.length > initialVisible, collapse to the first N with a
  // "show all" toggle. Omit to render every row (existing behaviour).
  initialVisible?: number
  // When provided, rows become clickable (used for drill-down).
  onRowClick?: (row: CategoricalBarRow) => void
  loading: boolean
  error: Error | null
  onRetry: () => void
}

export default function CategoricalBar({
  title,
  titleTooltip,
  subtitle,
  notice,
  headerAction,
  emptyMessage,
  rows,
  barColor = '#2a8d9c',
  formatValue,
  initialVisible,
  onRowClick,
  loading,
  error,
  onRetry,
}: CategoricalBarProps) {
  const { t } = useTranslation()
  const isEmpty = rows.length === 0
  const maxPct = isEmpty ? 1 : Math.max(...rows.map((r) => r.percent), 1)

  const collapsible = initialVisible != null && rows.length > initialVisible
  const [expanded, setExpanded] = useState(false)
  // Reset to collapsed when the underlying row set changes (e.g. filter change).
  const rowSignature = rows.map((r) => r.key).join('|')
  useEffect(() => {
    setExpanded(false)
  }, [rowSignature])

  const visibleRows = sliceVisible(rows, initialVisible, expanded)

  return (
    <WidgetCard
      title={title}
      titleTooltip={titleTooltip}
      subtitle={subtitle}
      headerAction={headerAction}
      notice={notice}
      loading={loading}
      error={error}
      empty={isEmpty}
      emptyMessage={emptyMessage}
      onRetry={onRetry}
      skeletonHeight={300}
    >
      <Stack spacing={1.5}>
        {visibleRows.map((r) => {
          const variance = varianceOf(r.count, r.previousCount)
          const clickable = Boolean(onRowClick)
          return (
            <Box
              key={r.key}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              onClick={clickable ? () => onRowClick!(r) : undefined}
              onKeyDown={
                clickable
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onRowClick!(r)
                      }
                    }
                  : undefined
              }
              sx={{
                cursor: clickable ? 'pointer' : 'default',
                borderRadius: 1,
                ...(clickable && { '&:hover': { backgroundColor: 'action.hover' } }),
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={r.label}
                >
                  {r.label}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexShrink: 0 }}>
                  <Typography variant="body2" color="text.secondary">
                    {formatValue
                      ? formatValue(r)
                      : `${r.count} (${r.percent.toFixed(1)}%)`}
                  </Typography>
                  {variance != null && (
                    <Typography
                      variant="caption"
                      color={variance >= 0 ? 'success.main' : 'error.main'}
                      sx={{ minWidth: 56, textAlign: 'right' }}
                    >
                      {formatVariance(variance)}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box
                sx={{
                  width: '100%',
                  height: 8,
                  backgroundColor: '#f0f0f0',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: `${(r.percent / maxPct) * 100}%`,
                    height: '100%',
                    backgroundColor: r.color ?? barColor,
                    borderRadius: 1,
                    transition: 'width 200ms ease',
                  }}
                />
              </Box>
            </Box>
          )
        })}
      </Stack>
      {collapsible && (
        <Button
          size="small"
          onClick={() => setExpanded((v) => !v)}
          sx={{ mt: 1, textTransform: 'none' }}
        >
          {expanded
            ? `▴ ${t('EXECUTIVE.CHART.COLLAPSE', 'ย่อ')}`
            : `▾ ${t('EXECUTIVE.CHART.SHOW_ALL', 'แสดงทั้งหมด ({{n}} ประเภท)', { n: rows.length })}`}
        </Button>
      )}
    </WidgetCard>
  )
}
